import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import get from 'lodash.get';
import { ViewTable } from '../layouts/viewTable';
import ViewIconItem from '../layouts/viewIconItem';
import { SystemOnlyDetail } from '../common/SystemOnlyBadge';
import { pageTypesStrings } from '../../constants/pageTypes';
import { getCountryName } from '../../helpers/entityOptionHelper';
import { useAppSelector } from '../../hooks/redux/useApp';
import { isSystemCredential } from '../../modules/session';

/**
 * The organisation behind the page as "Name (Country)", or just the name when the country is
 * unknown. The admin record has the organisation at the top level; older shapes nest it under
 * the campaign.
 */
export const getPageOrganisationLine = (
  entity: unknown
): string | undefined => {
  const organization =
    get(entity, 'organization') || get(entity, 'campaign.organization');
  const name: string | undefined = get(organization, 'name');
  if (!name) {
    return undefined;
  }
  const country = getCountryName(
    get(organization, 'country') || get(organization, 'address.country')
  );
  return country
    ? I18n.t('common.autocomplete.organisationWithCountry', {
        organisation: name,
        country
      })
    : name;
};

interface Props { entity: any; translationBase: string; }
export const PageEntityRelationships: React.FC<Props> = ({ entity, translationBase }) => {
  const isSystem = useAppSelector((state) => isSystemCredential(state.session));
  const team = get(entity,'team');
  const collection = get(entity,'collection');
  const campaign = get(entity,'campaign');
  const event = get(entity,'event');
  const organization = get(entity,'organization');
  const teamImage = get(team,'mainImageUrl') || get(campaign,'mainImageUrl');
  const collectionImage = get(collection,'mainImageUrl') || get(campaign,'mainImageUrl');
  const campaignImage = get(campaign,'mainImageUrl') || get(organization,'mainImageUrl');
  const eventImage = get(event,'mainImageUrl');
  // Campaigns that join an event share its name, so system credentials need the organisation to
  // tell which charity the page belongs to.
  const organisationLine = isSystem
    ? getPageOrganisationLine(entity)
    : undefined;
  const hasAny = team || collection || campaign || event || organization;
  if (!hasAny) return null;
  return (
    <div className="page-entity-relationships">
      <ViewTable title={<Translate value={`${translationBase}.page-information.title`} />}>
        {team && <ViewIconItem type={pageTypesStrings.TEAM} data={team} imageUrl={teamImage} />}
        {collection && <ViewIconItem type={pageTypesStrings.COLLECTION} data={collection} imageUrl={collectionImage} />}
        {campaign && (
          <ViewIconItem
            type={pageTypesStrings.CAMPAIGN}
            data={campaign}
            imageUrl={campaignImage}
            detail={
              organisationLine && (
                <SystemOnlyDetail>{organisationLine}</SystemOnlyDetail>
              )
            }
          />
        )}
        {event && <ViewIconItem type={pageTypesStrings.EVENT} data={event} imageUrl={eventImage} hideId />}
  {/* Organization display skipped (no pageTypesStrings.ORGANIZATION) */}
      </ViewTable>
    </div>
  );
};
export default PageEntityRelationships;
